import {
  Component,
  ElementRef,
  HostListener,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  untracked,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faCheck } from '@fortawesome/pro-regular-svg-icons';
import { DividerComponent } from '../divider/divider.component';
import { Subject } from 'rxjs';

// =============================================================================
// Dropdown Menu Types
// =============================================================================
export interface DropdownMenuItem {
  label: string;
  action: string;
  description?: string;
  variant?: 'default' | 'destructive';
  icon?: unknown;
  dividerBefore?: boolean;
  disabled?: boolean;
  active?: boolean;
  /** When defined, the item renders a leading checkbox reflecting this state (multi-select menus). */
  checked?: boolean;
  section?: string;
  sectionIcon?: unknown;
  /** Mark this item as part of a scrollable group. Items between two non-scrollable items will scroll together. */
  scrollable?: boolean;
}

interface MenuPosition {
  top?: number;
  bottom?: number;
  left?: number;
  right?: number;
}

// =============================================================================
// Dropdown Menu Component
// Uses position:fixed so it escapes overflow:hidden on table cells/containers.
// =============================================================================
@Component({
  selector: 'ams-dropdown-menu',
  standalone: true,
  imports: [CommonModule, FontAwesomeModule, DividerComponent],
  template: `
    <div class="ams-dropdown" [class.ams-dropdown--open]="isOpen()">
      <div class="ams-dropdown__trigger" (click)="toggleMenu($event)">
        <ng-content />
      </div>

      @if (isOpen() && menuPos()) {
        <div
          class="ams-dropdown__menu"
          [style.top.px]="menuPos()!.top"
          [style.bottom.px]="menuPos()!.bottom"
          [style.left.px]="menuPos()!.left"
          [style.right.px]="menuPos()!.right"
        >
          @for (group of itemGroups(); track $index) {
            @if (group.scrollable) {
              <div class="ams-dropdown__scroll-group">
                <ng-container *ngFor="let item of group.items">
                  <ng-container *ngTemplateOutlet="menuItemTpl; context: { $implicit: item }" />
                </ng-container>
              </div>
            } @else {
              @for (item of group.items; track item.action) {
                <ng-container *ngTemplateOutlet="menuItemTpl; context: { $implicit: item }" />
              }
            }
          }

          <ng-template #menuItemTpl let-item>
            @if (item.dividerBefore) {
              <ams-divider />
            }
            @if (item.section) {
              <div class="ams-dropdown__section-label">
                @if (item.sectionIcon) {
                  <span class="ams-dropdown__section-icon">
                    <fa-icon [icon]="$any(item.sectionIcon)" />
                  </span>
                }
                <span>{{ item.section }}</span>
              </div>
            }
            <button
              type="button"
              class="ams-dropdown__item"
              [class.ams-dropdown__item--destructive]="item.variant === 'destructive'"
              [class.ams-dropdown__item--active]="item.active"
              [class.ams-dropdown__item--disabled]="item.disabled"
              [disabled]="item.disabled"
              (click)="onItemClick(item, $event)"
            >
              @if (item.checked !== undefined) {
                <span class="ams-dropdown__item-check" [class.ams-dropdown__item-check--on]="item.checked">
                  @if (item.checked) {
                    <fa-icon [icon]="checkIcon" size="sm" />
                  }
                </span>
              }
              @if (item.icon) {
                <span class="ams-dropdown__item-icon">
                  <fa-icon [icon]="$any(item.icon)" />
                </span>
              }
              <span class="ams-dropdown__item-content">
                <span>{{ item.label }}</span>
                @if (item.description) {
                  <span class="ams-dropdown__item-description">{{ item.description }}</span>
                }
              </span>
            </button>
          </ng-template>

          <!-- Optional footer pinned below the items (e.g. an action button for a
               multi-select menu). Hidden when nothing is projected into it. -->
          <div class="ams-dropdown__footer"><ng-content select="[slot=menu-footer]" /></div>
        </div>
      }
    </div>
  `,
  styleUrl: './dropdown-menu.component.scss',
})
export class DropdownMenuComponent {
  private static readonly closeAll$ = new Subject<DropdownMenuComponent>();
  private readonly elementRef = inject(ElementRef);

  readonly items = input<DropdownMenuItem[]>([]);
  readonly align = input<'left' | 'right'>('right');
  /** Keep the menu open after a click — for multi-select/toggle menus (e.g. a column picker). */
  readonly closeOnSelect = input<boolean>(true);
  /**
   * Blocks opening (and closes an open menu). Needed when the trigger is an
   * ams-button: a disabled/loading ams-button is pointer-events:none, so the click
   * falls through to the trigger wrapper and would otherwise still open the menu.
   */
  readonly disabled = input<boolean>(false);

  readonly checkIcon = faCheck;

  readonly itemClick = output<{ action: string; event: MouseEvent }>();
  /** Fires each time the menu opens — lets a host reset per-open state (e.g. pre-checked items). */
  readonly opened = output<void>();

  constructor() {
    effect(() => {
      if (this.disabled()) untracked(() => this.close());
    });
    DropdownMenuComponent.closeAll$.subscribe(source => {
      if (source !== this && this.isOpen()) {
        this.isOpen.set(false);
        this.menuPos.set(null);
      }
    });
  }

  readonly itemGroups = computed<{ scrollable: boolean; items: DropdownMenuItem[] }[]>(() => {
    const groups: { scrollable: boolean; items: DropdownMenuItem[] }[] = [];
    let currentGroup: { scrollable: boolean; items: DropdownMenuItem[] } | null = null;

    for (const item of this.items()) {
      const isScrollable = !!item.scrollable;
      if (!currentGroup || currentGroup.scrollable !== isScrollable) {
        currentGroup = { scrollable: isScrollable, items: [] };
        groups.push(currentGroup);
      }
      currentGroup.items.push(item);
    }

    return groups;
  });

  readonly isOpen = signal(false);
  readonly menuPos = signal<MenuPosition | null>(null);

  toggleMenu(event: MouseEvent): void {
    // Before stopPropagation: a click on a disabled trigger should still reach the
    // document listener that closes any other open menu.
    if (this.disabled()) return;
    event.stopPropagation();

    if (this.isOpen()) {
      this.isOpen.set(false);
      this.menuPos.set(null);
      return;
    }

    // Close all other open dropdowns
    DropdownMenuComponent.closeAll$.next(this);

    const trigger = this.elementRef.nativeElement.querySelector(
      '.ams-dropdown__trigger',
    ) as HTMLElement;
    const rect = trigger.getBoundingClientRect();
    const gap = 4;
    const estimatedMenuHeight = 250;
    const openUpward = window.innerHeight - rect.bottom < estimatedMenuHeight;

    if (this.align() === 'right') {
      this.menuPos.set({
        top:    openUpward ? undefined : rect.bottom + gap,
        bottom: openUpward ? window.innerHeight - rect.top + gap : undefined,
        right:  window.innerWidth - rect.right,
      });
    } else {
      this.menuPos.set({
        top:    openUpward ? undefined : rect.bottom + gap,
        bottom: openUpward ? window.innerHeight - rect.top + gap : undefined,
        left:   rect.left,
      });
    }

    this.isOpen.set(true);
    this.opened.emit();
  }

  /** Closes the menu — for a host action that should dismiss it (e.g. a footer button). */
  close(): void {
    this.isOpen.set(false);
    this.menuPos.set(null);
  }

  onItemClick(item: DropdownMenuItem, event: MouseEvent): void {
    event.stopPropagation();
    if (item.disabled) return;
    if (this.closeOnSelect()) {
      this.isOpen.set(false);
      this.menuPos.set(null);
    }
    this.itemClick.emit({ action: item.action, event });
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.elementRef.nativeElement.contains(event.target as Node)) {
      this.isOpen.set(false);
      this.menuPos.set(null);
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.isOpen.set(false);
    this.menuPos.set(null);
  }

  @HostListener('window:scroll')
  onScroll(): void {
    if (this.isOpen()) {
      this.isOpen.set(false);
      this.menuPos.set(null);
    }
  }
}
